import { types } from 'mobx-state-tree';
import { fetchPoiList } from './flows/fetch-poi-list';

export const PoiCategory = types
  .model({
    _id: types.string,
    name: types.string,
    color: types.string,
    selected: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    toggleSelection: () => {
      self.selected = !self.selected;
    },
  }));

export const PoiAddress = types.model({
  street: types.maybeNull(types.string),
  number: types.maybeNull(types.string),
  zipCode: types.maybeNull(types.string),
  country: types.maybeNull(types.string),
  city: types.maybeNull(types.string),
});

export const PoiImage = types.model({
  url: types.string,
  title: types.maybeNull(types.string),
  source: types.maybeNull(types.string),
  sourceUrl: types.maybeNull(types.string),
  author: types.maybeNull(types.string),
  authorUrl: types.maybeNull(types.string),
  license: types.maybeNull(types.string),
  licenseUrl: types.maybeNull(types.string),
});

export const Copyright = types.model({
  title: types.maybeNull(types.string),
  source: types.maybeNull(types.string),
  sourceUrl: types.maybeNull(types.string),
  author: types.maybeNull(types.string),
  authorUrl: types.maybeNull(types.string),
  license: types.maybeNull(types.string),
  licenseUrl: types.maybeNull(types.string),
});

export const Point = types.model({
  coordinates: types.array(types.number),
});

export const Translation = types.model({
  title: types.string,
  description: types.maybeNull(types.string),
  openingHours: types.maybeNull(types.string),
  translatedFrom: types.maybeNull(types.string),
  translatedAt: types.maybeNull(types.string),
});

export const Poi = types
  .model({
    id: types.string,
    categories: types.array(PoiCategory),
    location: Point,
    address: types.maybeNull(PoiAddress),
    url: types.maybeNull(types.string),
    eMail: types.maybeNull(types.string),
    phone: types.maybeNull(types.string),
    provider: types.string,
    rating: types.maybeNull(types.number),
    localRating: types.maybeNull(types.number),
    images: types.array(PoiImage),
    copyright: Copyright,
    en: types.maybeNull(Translation),
    de: types.maybeNull(Translation),
    nl: types.maybeNull(Translation),
    fr: types.maybeNull(Translation),
  })
  .views((self) => ({
    get previewImageUrl() {
      return self.images.length ? self.images[0]?.url : null;
    },
    get previewImage() {
      return self.images.length ? self.images[0] : null;
    },
  }));

export const LoadingState = types.enumeration([
  'init',
  'done',
  'loading',
  'error',
]);

export const LoadingStates = {
  init: 'init',
  done: 'done',
  loading: 'loading',
  error: 'error',
};

export const PoiStore = types
  .model({
    status: types.optional(LoadingState, LoadingStates.init),
    list: types.optional(types.array(Poi), []),
    active: types.optional(types.maybeNull(Poi), null),
    hasClearFlag: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    fetch: (...args) => fetchPoiList(self, ...args),
    clear: () => (self.list = []),
    setClearFlag: (newState) => (self.hasClearFlag = newState),
    setActive: (poi) => (self.active = JSON.parse(JSON.stringify(poi))),
    setInactive: () => (self.active = null),
    add: (poi) => {
      const isInList = self.list.find((item) => item.id === poi.id);

      if (!isInList) self.list.push(JSON.parse(JSON.stringify(poi)));
    },
  }));

export const initialState = {
  list: [
    {
      address: { city: 'Piazza del Colosseo 1, Rome' },
      en: {
        title: 'Colosseum',
        description:
          'Built under Emperor Vespasian in A.D. 70-72 and completed by his son Titus 10 years later, the Flavian Amphitheatre or the Colosseum is a massive stone arena, which at its peak, could accommodate 80,000 spectators. \r\n\r\nAs in the movie "Gladiator", it has hosted violent and brutal displays of gladiatorial combats and wild animal fights, all just for the delight of crowds. Inauguration lasted one hundred days, and approximately 9,000 animals and 2,000 gladiators were killed during the event. \r\n\r\nToday, the Colosseum is Rome\'s most visited sight, which never fails to leave visitors awe-struck. Be sure to also come here at night when it looks even more magical.',
        openingHours: 'Daily 9:30am–6:30pm',
      },
      de: {
        title: 'Kolosseum',
        description:
          'Wahrscheinlich Roms bekanntestes Wahrzeichen. Das massive Amphitheater wurde zu der Zeit des Imperator Vespasian, in den Jahren 70-72 begonnen und 10 Jahre später von seinem Sohn Titus fertiggestellt. Wie in dem Film "Gladiator" wurden hier blutige und brutale Kämpfe für die Unterhaltung der Massen ausgetragen, die Menschen und wilde Tiere beinhalteten. Die Einweihung dauerte 100 Tage an und kostete 9.000 Tiere und 2.000 Gladiatoren das Leben. Zu seinen Glanzzeiten besuchten bis zu 87.000 Zuschauer das Kolosseum. \r\n\r\nHeute is es Roms meistbesuchte Attraktion, die jeden Besucher in etwas Ehrfurcht versetzt.',
        openingHours: 'Mon. - Son. 8:30 - 19:00.',
      },
      fr: {
        title: 'Le Colisée',
        description:
          "Le Colisée est l'un des monuments les plus impressionnants de Rome et l'un des lieux les plus célèbres et reconnu au monde. Beaucoup de gens et d'animaux y ont perdu la vie ici au cours de jeux tels que les combats de gladiateurs et des combats entre animaux sauvages. L'empereur Vespasien commença la construction du Colisée mais il fut achevé par son fils Titus. Le bâtiment a été achevé en 80 après JC. Son inauguration a duré cent jours, et durant cet évènement, environ 9.000 animaux et 2.000 gladiateurs ont été tués. À son apogée, cet endroit accueillait 87 000 spectateurs. Aujourd'hui, c'est le lieu le plus visité de Rome.",
        openingHours: 'Du lundi au dimanche de 8h30 à 19h',
      },
      _id: '62443fbc1c8729f36f871a38',
      id: 'ArrivalGuides17738',
      categories: [
        {
          name: 'sight',
          color: '#C00CAA',
          active: true,
          selected: true,
          _id: '62442cf9269e8eed7f88e872',
          __v: 0,
        },
      ],
      eMail: 'pa-colosseo@beniculturali.it',
      images: [
        {
          title: 'Colosseum, Rome, Italy',
          author: 'Sunilbhar',
          authorUrl: 'http://commons.wikimedia.org/wiki/User:Sunilbhar',
          license: 'CC BY-SA 3.0',
          licenseUrl: 'http://creativecommons.org/licenses/by-sa/3.0',
          url: 'https://media-cdn.sygictraveldata.com/media/612664395a40232133447d33247d3832343830383835.jpg',
          _id: '62a6f61303cd4c5aaf8c4d6d',
        },
        {
          title:
            'View of inside of the Colosseum, through an arch. Rome, Italy.',
          author: 'Jebulon',
          authorUrl: 'http://commons.wikimedia.org/wiki/User:Jebulon',
          license: 'CC0',
          licenseUrl:
            'http://creativecommons.org/publicdomain/zero/1.0/deed.en',
          url: 'https://media-cdn.sygictraveldata.com/media/612664395a40232133447d33247d3832343830383737.jpg',
          _id: '62a6f61303cd4c5aaf8c4d6e',
        },
      ],
      localRating: 10,
      location: { type: 'Point', coordinates: [12.4922309, 41.8902102] },
      phone: '+39 06 3996 7700',
      provider: 'ArrivalGuides',
      rating: 10,
      sunnyRating: 10,
      url: 'www.parcocolosseo.it',
      copyright: {
        source: 'Sygic Maps',
        sourceUrl: 'https://https://www.sygic.com/',
      },
    },
  ],
};
